import { Parse, runCloudFunction } from '@/store/ParseUtils'
import * as Sentry from '@sentry/vue'
import type { Study, Teach } from '@pocketprep/types'
import { userModule } from '@/store/user/module'
import { groupModule } from '@/store/group/module'
import { statsModule } from '@/store/stats/module'
import { studentModule } from '@/store/student/module'
import { instructorModule } from '@/store/instructor/module'
import { licenseModule } from '@/store/license/module'
import { analyticsModule } from '@/store/analytics/module'
import { resetState } from '@/store/reset'
import { organizationModule } from '@/store/organization/module'
import { examModule } from '@/store/exam/module'
import { isSupportWindow } from '@/utils'

type TeachSignInWithCode = (params: {
    email: string
    code: string
}) => { sessionToken: string }

const resetPassword = async () => {
    const user = Parse.User.current<Study.Class.User>()
    if (!user) {
        throw new Error('resetPassword: No current user found.')
    }
    await Parse.User.requestPasswordReset(user.get('username'))
    return
}

const createUser = async ({ username, password, firstName, lastName }: {
    username: string
    password: string
    firstName: string
    lastName: string
}) => {
    const user = (await Parse.User.signUp<Study.Class.User>(
        username.toLowerCase(),
        password,
        {
            email: username.toLowerCase(),
            firstName,
            lastName,
        }
    )).toJSON()
    userModule.state.user = user
    await afterAuth()

    return user
}

const updateUser = async ({ firstName, lastName, email, currentExamGuid }: {
    firstName?: string
    lastName?: string
    email?: string
    currentExamGuid?: string
}) => {
    const currentUser = Parse.User.current<Study.Class.User>()
    if (!currentUser) {
        throw new Error('updateUser: Could not find current user')
    }

    if (firstName) {
        currentUser.set({
            firstName,
        })
    }
    if (lastName) {
        currentUser.set({
            lastName,
        })
    }
    if (email) {
        currentUser.set({
            email,
            username: email,
        })
    }
    if (currentExamGuid) {
        currentUser.set({
            currentExamGuid,
        })
    }

    await currentUser.save()
    const updatedUser = Parse.User.current<Study.Class.User>()
    if (!updatedUser) {
        throw new Error('updateUser: Could not find updated user')
    }

    userModule.state.user = updatedUser.toJSON()
    updateIntercom()

    return updatedUser.toJSON()
}

const updateQuizSettings = async (
    quizSettings: { [T in keyof Study.Class.UserQuizSettings]: Study.Class.UserQuizSettings[T] }
) => {
    const user: Study.Class.User | undefined = Parse.User.current()
    const stateUser = userModule.state.user

    if (!user || !stateUser) {
        throw new Error('No current user found.')
    }

    const updatedQuizSettings = {
        ...stateUser.quizSettings,
        ...quizSettings,
    }

    user.set('quizSettings', updatedQuizSettings)
    await user.save()

    userModule.state.user = user.toJSON()
}

const createUserExamMetadata = async ({ examGuid, examVersion }: { examGuid: string; examVersion: string }) => {
    const currentUser = Parse.User.current<Study.Class.User>()
    if (!currentUser) {
        throw new Error('createUserExamMetadata: Could not find current user')
    }

    // Check whether user already has a UEM record for the examGuid
    const uem = await new Parse.Query<Study.Class.UserExamMetadata>('UserExamMetadata')
        .equalTo('user', { __type: 'Pointer', objectId: currentUser.id, className: '_User' })
        .equalTo('examGuid', examGuid)
        .first()

    // If user already has a UEM record, do nothing
    if (uem) {
        return
    }

    const newUserExamMetadata = new Parse.Object<Study.Class.UserExamMetadataPayload>('UserExamMetadata', {
        examGuid,
        examVersion,
        disabledSubjects: [],
        flaggedQuestions: [],
        upgradesSeen: [],
        user: currentUser,
    })
    const userACL = new Parse.ACL(currentUser)
    newUserExamMetadata.setACL(userACL)

    await newUserExamMetadata.save()
}

const signIn = async ({ username, password }: { username: string; password: string }) => {
    const user = (await Parse.User.logIn<Study.Class.User>(username.toLowerCase(), password)).toJSON()
    userModule.state.user = user
    await afterAuth()

    return user
}

const signInWithCode = async ({ email, code }: { email: string; code: string }) => {
    const { sessionToken } = await runCloudFunction<TeachSignInWithCode>('teachSignInWithCode', { email, code })

    return signInWithSession({ sessionToken })
}

const signInWithSession = async ({ sessionToken }: { sessionToken: string }) => {
    const parseUser = await Parse.User.become<Study.Class.User>(sessionToken)
    const user = parseUser.toJSON()

    userModule.state.user = user
    // Reset so any in-flight fetch for the previous user is not reused (router may have
    // started it before Teach.vue onMounted applied this session).
    resetState()
    await afterAuth()

    return user
}

const afterAuth = async () => {
    const user = userModule.state.user
    if (user) {
        Sentry.setUser({
            id: user.objectId,
            email: user.email,
        })
        analyticsModule.actions.setAmplitudeUser({
            id: user.objectId,
            email: user.email,
            createdAt: user.createdAt,
            currentOrgId: user.currentOrganization?.objectId,
        })

        await organizationModule.actions.fetchCurrentOrganization()
        const currentOrg = organizationModule.getters.getCurrentOrganization()
        if (currentOrg) {
            await Promise.all([
                examModule.actions.fetchCurrentOrgExams(),
                groupModule.actions.fetchCurrentOrgGroups(),
                studentModule.actions.fetchCurrentOrgStudents(),
                licenseModule.actions.fetchCurrentOrgLicenses(),
                examModule.actions.fetchExamMetadata(),
                instructorModule.actions.fetchCurrentUserOrgInstructors(),
            ])
            organizationModule.actions.validateOrgState()
        }

        bootIntercom()
    }
}

const signOut = async () => {
    try {
        await Parse.User.logOut()
    } catch (e) { /* noop */ }

    if (import.meta.env.VUE_APP_INTERCOM_APP_ID && !isSupportWindow()) {
        window.Intercom('shutdown')
    }
    resetState()

    Sentry.setUser({
        id: undefined,
        email: undefined,
    })

    analyticsModule.actions.amplitudeReset()
}

const sendMagicEmail = async ({ email }: { email: string }) => {
    await runCloudFunction<Study.Cloud.sendMagicEmail>('sendTeachMagicEmail', {
        email: email.toLowerCase(),
    })
}

const updateCurrentOrganization = async (organization: Teach.Class.OrganizationJSON) => {
    const currentUser = Parse.User.current<Study.Class.User>()
    if (!currentUser) {
        throw new Error('updateCurrentOrganization: Could not find current user')
    }

    (currentUser as Parse.Object).set('currentOrganization', {
        __type: 'Pointer',
        className: 'Organization',
        objectId: organization.objectId,
    })
    await currentUser.save()

    userModule.state.user = currentUser.toJSON()

    analyticsModule.actions.setAmplitudeUser({
        id: userModule.state.user.objectId,
        email: userModule.state.user.email,
        createdAt: userModule.state.user.createdAt,
        currentOrgId: userModule.state.user.currentOrganization?.objectId,
    })

    groupModule.actions.updateCurrentGroupId(null)

    studentModule.actions.updateCurrentStudentId(null)

    statsModule.actions.resetStudentAggStats()
    statsModule.actions.resetGroupAggStats()

    await Promise.all([
        groupModule.actions.fetchCurrentGroup(),
        groupModule.actions.fetchCurrentOrgGroups(),
        studentModule.actions.fetchCurrentOrgStudents(),
        studentModule.actions.fetchCurrentOrgStudentUsers(),
        instructorModule.actions.fetchCurrentOrgInstructors(),
        instructorModule.actions.fetchCurrentOrgInstructorUsers(),
        licenseModule.actions.fetchCurrentOrgLicenses(),
    ])
}

const dismiss = (dismissableId: string) => {
    userModule.state.dismissables[dismissableId] = true
}

const googleAnalyticsTrack = async (eventName: string, eventProperties?: Record<string, string | number | boolean>) => {
    if (import.meta.env.VUE_APP_GOOGLE_ANALYTICS_ID && !isSupportWindow()) {
        gtag('event', eventName, {
            ...eventProperties,
        })
    }
    return Promise.resolve()
}

const trackEvent = async (eventName: string, eventProperties?: Record<string, string | number | boolean>) => {
    // Helper to send an event to multiple destinations
    await Promise.all([
        googleAnalyticsTrack(eventName, eventProperties),
    ])
}

const updateWebConfig = async (webConfig: { [T in keyof Study.Class.UserWebConfig]: Study.Class.UserWebConfig[T] }) => {
    if (isSupportWindow()) {
        return
    }
    const user: Study.Class.User | undefined = Parse.User.current()
    const stateUser = userModule.state.user

    if (!user || !stateUser) {
        throw new Error('No current user found.')
    }

    const updatedWebConfig = {
        ...stateUser.webConfig,
        ...webConfig,
    }

    user.set('webConfig', updatedWebConfig)
    await user.save()

    userModule.state.user = user.toJSON()
}

const getSessionToken = () => {
    return Parse.User.current()?.getSessionToken()
}

/**
 * Intercom
 */

const fetchIntercomData = async () => {
    const org = await organizationModule.actions.fetchCurrentOrganization()

    if (org) {
        await Promise.all([
            groupModule.actions.fetchCurrentOrgGroups(),
            instructorModule.actions.fetchCurrentOrgInstructors(),
            examModule.actions.fetchCurrentOrgExams(),
        ])
    }
}

const bootIntercom = async () => {
    if (import.meta.env.VUE_APP_INTERCOM_APP_ID && !isSupportWindow()) {
        await fetchIntercomData()
        const intercomSettings = userModule.getters.intercomSettings()
        window.Intercom('boot', intercomSettings)
    }
}

const updateIntercom = async () => {
    if (import.meta.env.VUE_APP_INTERCOM_APP_ID && !isSupportWindow()) {
        await fetchIntercomData()
        const intercomSettings = userModule.getters.intercomSettings()
        window.Intercom('update', intercomSettings)
    }
}

export type UTMProperties = {
    campaign: string
    content: string
    medium: string
    source: string
    term: string
}
type TUpsertHubspotLead = (params: {
    organizationId?: string
    utm?: UTMProperties
}) => Promise<void>

const upsertHubspotLead = async (params: { organizationId?: string; utm?: UTMProperties }) => {
    return runCloudFunction<TUpsertHubspotLead>('upsertHubspotLead', {
        organizationId: params.organizationId,
        utm: params.utm,
    })
}

export default {
    resetPassword,
    createUser,
    updateUser,
    updateQuizSettings,
    createUserExamMetadata,
    signIn,
    signInWithCode,
    signInWithSession,
    signOut,
    sendMagicEmail,
    updateCurrentOrganization,
    dismiss,
    googleAnalyticsTrack,
    trackEvent,
    updateWebConfig,
    getSessionToken,
    upsertHubspotLead,
    bootIntercom,
    updateIntercom,
}
